/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav P�ibyl (pribyl@cadwork.cz)
 *
 */

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import WeightedAverageColorAccShader from "App/render/webgl/shaders/wavg/wavg_color_acc";
import WeightedAverageColorAccNoTextureShader from "App/render/webgl/shaders/wavg/wavg_color_acc_no_texture";
import Factory from "App/render/webgl/passes/factory";
import {
    OpaqueBinTypeName, OpaqueNoTextureBinTypeName,
    TransparentBinTypeName, TransparentInternalBinTypeName,
    TransparentNoTextureBinTypeName, TransparentNoTextureInternalBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

export default 
class ColorAccumulation extends Pass {

    public static TypeName: string = "ColorAccumulation";

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this._requireLighting = true;
    }


    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ColorAccumulation {
        return new ColorAccumulation(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(TransparentBinTypeName, shaders.get(WeightedAverageColorAccShader.TypeName));
        this.attachShaderToBin(TransparentNoTextureBinTypeName, shaders.get(WeightedAverageColorAccNoTextureShader.TypeName));
        this.attachShaderToBin(TransparentInternalBinTypeName, shaders.get(WeightedAverageColorAccShader.TypeName));
        this.attachShaderToBin(TransparentNoTextureInternalBinTypeName, shaders.get(WeightedAverageColorAccNoTextureShader.TypeName));
        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(WeightedAverageColorAccShader.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(WeightedAverageColorAccNoTextureShader.TypeName));
    }

    public render(aView, aOpaqueDepthBuffer, aDepthAccBuffer, aOutputBuffer) {
        this.view = aView;

        let canvas = this._context.getCanvas();
        let contentSize = canvas.getContentSize();
        let gl = this._context.getWebGLRenderingContext();

        let qualityFactor = this._sceneAPI.renderSettingsModule.getQualityPerformanceFactor();
        let contentSizeScaledRecip = [qualityFactor / contentSize[0], qualityFactor / contentSize[1]];

        aOutputBuffer.bind();

        // the opaque depth is rendered with some scale so we need to strech the depth buffer back to screen size here
        let scale = aOutputBuffer.parameters.scale;
        gl.viewport(aView.position[0] * scale, aView.position[1] * scale, aView.size[0] * scale, aView.size[1] * scale);
        gl.scissor(aView.position[0] * scale, aView.position[1] * scale, aView.size[0] * scale, aView.size[1] * scale);

        gl.clear(gl.COLOR_BUFFER_BIT);

        // set some shaded uniforms for shaders used in the rendered bins here
        for (let key in this._shadersToBins) {
            let shaderRenderable = this._shadersToBins[key].get(0);
            shaderRenderable.render();

            gl.uniform2fv(shaderRenderable.uniformLocations.u_canvas_size_recip, contentSizeScaledRecip);

            gl.activeTexture(gl.TEXTURE3);
            gl.bindTexture(gl.TEXTURE_2D, aDepthAccBuffer.renderable.getColorTexture());
            gl.uniform1i(shaderRenderable.uniformLocations.u_tex_acc_depth, 3);

            gl.activeTexture(gl.TEXTURE2);
            gl.bindTexture(gl.TEXTURE_2D, aOpaqueDepthBuffer.renderable.getColorTexture());
            gl.uniform1i(shaderRenderable.uniformLocations.u_tex_opaque_depth, 2);

            gl.uniformMatrix3fv(shaderRenderable.uniformLocations.u_normal_mat, false, aView.getCamera().getViewMatrix3IT());
        }

        let bins = this._renderGraph.getRenderBins();

        let allOpaque =
            !this._sceneAPI.renderSettingsModule.getElementTransparencyEnabled() ||
            (this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverride() &&
            (this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverrideValue() === 0.0));

        if (!allOpaque) {
            bins.get(TransparentBinTypeName).render(this);
            bins.get(TransparentNoTextureBinTypeName).render(this);
        }

        let allTransparent =
            this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverride() &&
            (this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverrideValue() > 0.0);

        if (allTransparent) {
            bins.get(OpaqueBinTypeName).render(this);
            bins.get(OpaqueNoTextureBinTypeName).render(this);
        }

        bins.get(TransparentInternalBinTypeName).render(this);
        bins.get(TransparentNoTextureInternalBinTypeName).render(this);

        this.unbindShader();

        this.view = null;
    }
}

Factory.register(ColorAccumulation);